import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, Alert, CircularProgress, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReferenceLabelPicker } from './ReferenceLabelPicker';
import { referenceLabelService } from '@/services/api/referenceLabelService';
import { features } from '@/config/features';
import logger from '@/lib/logger';
import type { ReferenceLabelValue } from '@/types/referenceLabels';

interface Channel {
  channelId: number;
  pathogenName: string;
}

interface ReferenceLabelsPanelProps {
  testId?: string;
  channels?: Array<{ pathogenName?: string; pathogen_name?: string; channelId?: number; channel_id?: number }>;
  mode?: 'measurement' | 'view';
}

export const ReferenceLabelsPanel: React.FC<ReferenceLabelsPanelProps> = ({
  testId,
  channels,
  mode: _mode,
}) => {
  const [labels, setLabels] = useState<Record<number, ReferenceLabelValue | null>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLabelsRef = useRef<Record<number, ReferenceLabelValue | null>>({});
  const pendingNotesRef = useRef<Record<number, string>>({});
  const [concentrations, setConcentrations] = useState<Record<number, number | undefined>>({});
  const pendingConcentrationsRef = useRef<Record<number, number | undefined>>({});

  const channelList = useMemo<Channel[]>(() => {
    if (!channels) return [];
    return channels.map((ch, i) => ({
      channelId: ch.channelId ?? ch.channel_id ?? i + 1,
      pathogenName: ch.pathogenName ?? ch.pathogen_name ?? `Channel ${i + 1}`,
    }));
  }, [channels]);

  // Load existing labels when testId changes
  useEffect(() => {
    if (!testId || !features.enableResearchFeatures) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const existing = await referenceLabelService.getLabels(testId);
        if (cancelled) return;
        const labelMap: Record<number, ReferenceLabelValue | null> = {};
        const noteMap: Record<number, string> = {};
        for (const l of existing) {
          labelMap[l.channelId] = l.referenceLabel;
          if (l.notes) noteMap[l.channelId] = l.notes;
        }
        const concentrationMap: Record<number, number | undefined> = {};
        for (const l of existing) {
          if (l.concentration != null) concentrationMap[l.channelId] = l.concentration;
        }
        setLabels(labelMap);
        setNotes(noteMap);
        setConcentrations(concentrationMap);
        pendingLabelsRef.current = labelMap;
        pendingNotesRef.current = noteMap;
        pendingConcentrationsRef.current = concentrationMap;
      } catch (err: any) {
        if (cancelled) return;
        // 404 means feature disabled or test not found — not a real error for the user
        if (err?.response?.status === 404) {
          setLabels({});
        } else {
          logger.error('[ReferenceLabelsPanel] Failed to load labels:', err);
          setError('Failed to load reference labels');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [testId]);

  const saveLabels = useCallback(async (
    testId: string,
    labelMap: Record<number, ReferenceLabelValue | null>,
    noteMap: Record<number, string>,
    concentrationMap: Record<number, number | undefined>
  ) => {
    setSaving(true);
    setError(null);
    try {
      const allChannelIds = new Set([
        ...Object.keys(labelMap).map(Number),
        ...Object.keys(noteMap).map(Number),
        ...Object.keys(concentrationMap).map(Number),
      ]);
      const labelArray = [...allChannelIds]
        .filter((channelId) =>
          labelMap[channelId] !== undefined ||
          noteMap[channelId] ||
          concentrationMap[channelId] !== undefined
        )
        .map((channelId) => ({
          channelId,
          referenceLabel: labelMap[channelId] ?? null,
          notes: noteMap[channelId] || undefined,
          concentration: concentrationMap[channelId],
        }));
      if (labelArray.length > 0) {
        await referenceLabelService.saveLabels(testId, labelArray);
        setLastSaved(new Date());
      }
    } catch (err) {
      logger.error('[ReferenceLabelsPanel] Failed to save labels:', err);
      setError('Failed to save reference labels');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleChange = useCallback((channelId: number, value: ReferenceLabelValue | null) => {
    if (!testId) return;

    const updatedLabels = { ...pendingLabelsRef.current, [channelId]: value };
    pendingLabelsRef.current = updatedLabels;
    setLabels(updatedLabels);

    // Cancel any pending debounce; save immediately on explicit label change
    if (debounceRef.current) clearTimeout(debounceRef.current);
    saveLabels(testId, pendingLabelsRef.current, pendingNotesRef.current, pendingConcentrationsRef.current);
  }, [testId, saveLabels]);

  const handleNoteChange = useCallback((channelId: number, note: string) => {
    const updatedNotes = { ...pendingNotesRef.current, [channelId]: note };
    pendingNotesRef.current = updatedNotes;
    setNotes(updatedNotes);
  }, []);

  const handleNoteSave = useCallback(() => {
    if (!testId) return;
    saveLabels(testId, pendingLabelsRef.current, pendingNotesRef.current, pendingConcentrationsRef.current);
  }, [testId, saveLabels]);

  const handleConcentrationChange = useCallback((channelId: number, value: number | undefined) => {
    const updated = { ...pendingConcentrationsRef.current, [channelId]: value };
    pendingConcentrationsRef.current = updated;
    setConcentrations(updated);
  }, []);

  const handleConcentrationSave = useCallback(() => {
    if (!testId) return;
    saveLabels(testId, pendingLabelsRef.current, pendingNotesRef.current, pendingConcentrationsRef.current);
  }, [testId, saveLabels]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  if (!features.enableResearchFeatures || !testId || channelList.length === 0) return null;

  return (
    <Box>
      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
            <ScienceIcon fontSize="small" color="primary" />
            <Typography variant="h6">Reference Labels</Typography>
            {saving && <CircularProgress size={14} sx={{ ml: 1 }} />}
            {lastSaved && !saving && (
              <Chip
                label={`Saved ${lastSaved.toLocaleTimeString()}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2, py: 0 }} icon={false}>
            <Typography variant="caption">
              Research only — assign ground truth labels for ML training.
            </Typography>
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {channelList.map((ch) => (
                <ReferenceLabelPicker
                  key={ch.channelId}
                  channelId={ch.channelId}
                  pathogenName={ch.pathogenName}
                  value={labels[ch.channelId]}
                  note={notes[ch.channelId]}
                  concentration={concentrations[ch.channelId]}
                  onChange={(val) => handleChange(ch.channelId, val)}
                  onNoteChange={(note) => handleNoteChange(ch.channelId, note)}
                  onNoteSave={() => handleNoteSave()}
                  onConcentrationChange={(val) => handleConcentrationChange(ch.channelId, val)}
                  onConcentrationSave={() => handleConcentrationSave()}
                  disabled={saving}
                />
              ))}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
